<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    :disabled-submit="initRegistrationSubstituteAthletesForm.submitNotAllowed"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:registrationSubstituteAthletesForm.heading",
                "Substitute athlete"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:registrationSubstituteAthletesForm.explanation.text",
                "You are going to substitute athlete for one or several entries and products in the entry form."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            variant="text"
            :disabled="formSubmitted"
            class="mx-0"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="registrationLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="registration">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />
        <AppEntryRegistrationOverview
          v-bind="{ registration, competition }"
          hide-competition-header
        >
          <template #athleteNamesHeader>
            {{
              $t(
                "events:registrationSubstituteAthletesForm.athleteNames.label",
                "Athlete"
              )
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t(
                "events:registrationSubstituteAthletesForm.horseNames.label",
                "Horse"
              )
            }}
          </template>

          <template #additionals>
            <div>
              <b>{{
                $t(
                  "events:registrationSubstituteAthletesForm.competitions.label",
                  "Competitions"
                )
              }}</b>
            </div>
            <div data-test="allRegisteredClasses">
              <AppShowEntryClassChip
                v-for="e in entries"
                :key="e.id"
                :entry="e"
                class="mb-2 mr-2"
              >
                {{ e.competition.code }}
              </AppShowEntryClassChip>
            </div>
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <v-form :disabled="initFormLoading">
          <div v-if="initFormLoading" class="mb-4">
            <v-progress-linear indeterminate />
          </div>

          <div class="mb-4">
            <b>{{
              $t(
                [
                  "events:registrationSubstituteAthletesForm.selectNewAthlete.subheading",
                  "Substitute entered athlete {\{ personName }\} with new athlete:"
                ],
                {
                  personName: currentPersonName
                }
              )
            }}</b>
          </div>
          <AppSelectPerson
            v-model="substituteWithPerson"
            :input-props="{
              label: $t(
                'events:registrationSubstituteAthletesForm.substituteWithPersonId.label',
                'Select new athlete'
              ),
              returnObject: true
            }"
            data-test="substituteWithPersonId"
            :person-fragment="selectPersonFragments"
          />

          <br />

          <template v-if="substituteWithPerson">
            <div class="mb-4">
              <b>{{
                $t(
                  "events:registrationSubstituteAthletesForm.substituteEntryIds.label",
                  "Select competitions and products, where the horse should be substituted"
                )
              }}</b>
            </div>

            <table class="entsys--table mb-4" data-test="possibles">
              <colgroup>
                <col style="width: 45px" />
                <col />
              </colgroup>
              <tbody>
                <tr v-for="e in possibleEntriesPrep" :key="e.id + '-possible'">
                  <td>
                    <v-checkbox
                      v-model="form.substituteEntryIds"
                      data-test="substituteEntry"
                      hide-details
                      multiple
                      :value="e.id"
                    />
                  </td>
                  <td>
                    {{ showEntryCompetitionDetails(e.competition) }}
                  </td>
                </tr>

                <tr
                  v-for="e in possibleEventProductsPrep"
                  :key="e.id + '-possible-product'"
                >
                  <td>
                    <v-checkbox
                      v-model="form.substituteRegistrationEventProductIds"
                      data-test="substituteEventProduct"
                      hide-details
                      multiple
                      class="mt-0 pt-0"
                      :value="e.id"
                    />
                  </td>
                  <td>
                    {{ e.eventProduct.name }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="mb-4">
              <b>{{
                $t(
                  "events:registrationSubstituteAthletesForm.resultOfSubstitution.subheading",
                  "Result of substitution:"
                )
              }}</b>
            </div>

            <table class="entsys--table mb-4" data-test="possibleResults">
              <tbody>
                <tr
                  v-for="e in possibleChanges.possibleEntries"
                  :key="e.id + '-possible-change'"
                >
                  <td>
                    <AppShowPersonNameWithIdentifiers
                      :person="e.athlete"
                      no-link
                    />
                  </td>
                  <td>
                    <AppShowHorseName
                      :horse="e.horse"
                      show-identifier
                      no-link
                    />
                  </td>
                  <td>
                    <AppShowEntryClassChip :entry="e">
                      {{ e.competition.code }}
                    </AppShowEntryClassChip>
                  </td>
                </tr>

                <tr
                  v-for="e in possibleChanges.possibleProducts"
                  :key="e.id + '-possible-change'"
                >
                  <td>
                    <AppShowPersonNameWithIdentifiers
                      :person="e.athlete"
                      no-link
                    />
                  </td>
                  <td>
                    <AppShowHorseName
                      :horse="e.horse"
                      show-identifier
                      no-link
                    />
                  </td>
                  <td>
                    {{ e.eventProduct.name }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </v-form>
        <AppAlertNotice center>
          <div class="mb-2">
            <b>{{
              $t(
                "events:registrationSubstituteAthletesForm.resultOfSubstitution.subheading",
                "Please note:"
              )
            }}</b>
          </div>

          <div
            v-html="
              $t(
                'events:registrationSubstituteAthletesForm.alertNote.generalAlert.text',
                `You are going to substitute athlete for selected entries and/or products. 
                  The payer (responsible) for entries and products will remain the same disregarding the change of athlete. 
                  Please note that maximum limits are not validated for this action. Ensure that maximum limits or/and other validation rules for entries are not violated.`
              )
            "
          />

          <div
            v-if="competitionFeebackMessages.length"
            data-test="comp-feedbacks"
            class="mt-4"
          >
            <ul>
              <li
                v-for="fm in competitionFeebackMessages"
                :key="fm.competition.id + '-competition-fm'"
              >
                <b> {{ showEntryCompetitionDetails(fm.competition) }}</b> -
                {{ fm.feedbackMessages }}
              </li>
            </ul>
          </div>
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:registrationSubstituteAthletesForm.confirmSubstitution.button",
          "Confirm substitution"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import {
  useEntryFeiActionsInjections,
  injectEntryFeiRegistrationQuery
} from "./compositions";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import { apolloClient } from "@/plugins/apollo";
import {
  useDisplayText,
  DisplayTextFragments
} from "@/composables/display_text";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import { upperCase, cloneDeep, sortBy, clone } from "lodash-es";
import AppShowEntryClassChip from "@AppShows/event/AppShowEntryClassChip.vue";
import { useCompetitionHelpers } from "@/composables/competition_helpers";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppShowPersonNameWithIdentifiers from "@AppShows/person/AppShowPersonNameWithIdentifiers.vue";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { useQuery } from "@vue/apollo-composable";

defineOptions({
  name: "EntryAthleteSubstitute"
});

const { fragments } = useEntryFeiActionsInjections();
const registrationQuery = injectEntryFeiRegistrationQuery();
const { competitionOrderFn } = useCompetitionHelpers();
const { displayText } = useDisplayText();

const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const competition = ref({}); // AppEntryRegistrationOverview wants prop be defined at least

const modal = ref(false);
const blockRegistrationQuery = ref(false);

const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);
const initRegistrationSubstituteAthletesForm = ref({
  formFields: [],
  submitNotAllowed: false
});

const form = ref({
  substituteRegistrationEventProductIds: [],
  substituteEntryIds: [],
  substituteWithPersonId: null,
  substituteWithPerson: null,
  substituteInRegistrationId: null
});

const registrationLoading = ref(false);

const registration = ref({
  athletes: [],
  horses: [],
  eventProducts: [],
  entries: []
});

const event = computed(() => {
  return registration.value?.event;
});

const selectPersonFragments = computed(() => {
  return gql`fragment _ on Person { ${AppShowPersonNameWithIdentifiers.fragments.person} }`;
});

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
    }

    modal.value = v;
  }
});

const substituteWithPerson = computed({
  get() {
    return form.value.substituteWithPerson;
  },
  set(v) {
    form.value.substituteWithPerson = v;
    form.value.substituteWithPersonId = v?.id ?? null;

    if (!form.value.substituteWithPersonId) {
      form.value = {
        substituteRegistrationEventProductIds: [],
        substituteEntryIds: [],
        substituteWithPersonId: null,
        substituteWithPerson: null,
        substituteInRegistrationId: registration.value.id
      };
    }
  }
});

const competitionFeebackMessages = computed(() => {
  const comps = registration.value.entries.map(e => e.competition);
  return cloneDeep(
    initRegistrationSubstituteAthletesForm.value
      ?.competitionsFeedbackMessages ?? []
  ).map(x => {
    return {
      competition: comps.find(c => x.competitionId == c.id),
      feedbackMessages: x.feedbackMessages.map(fm => displayText(fm)).join("; ")
    };
  });
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:registrationSubstituteAthletesForm.success.heading",
          "Athlete substitution"
        ),
        main: t(
          "events:registrationSubstituteAthletesForm.success.mainMessage.text",
          "Athlete substituted successfully"
        ),
        additional: t(
          "events:registrationSubstituteAthletesForm.success.additionalMessage.text",
          "Please note, the payer (responsible) for the entries and products will remain the same as in the initial entry."
        ),
        okBtn: t(
          "events:registrationSubstituteAthletesForm.success.ok.button",
          "OK"
        )
      };
    case "error":
      return {
        headline: t(
          "events:registrationSubstituteAthletesForm.failed.heading",
          "Athlete substitution"
        ),
        main: t(
          "events:registrationSubstituteAthletesForm.failed.mainMessage.text",
          "Athlete substitution failed"
        ),
        reason: t(
          "events:registrationSubstituteAthletesForm.failed.reason.text",
          "The failure was due the following reason:"
        ),
        additional: t(
          "events:registrationSubstituteAthletesForm.failed.additionalMessage.text",
          "Please contact technical support, if you need help on this matter."
        ),
        okBtn: t(
          "events:registrationSubstituteAthletesForm.failed.ok.button",
          "OK"
        )
      };
    default:
      return null;
  }
});

const currentPersonName = computed(() => {
  return upperCase(registration.value?.athletes[0]?.name ?? "");
});

const entries = computed(() => {
  const entries = cloneDeep(registration.value?.entries ?? []);
  return entries.sort((a, b) =>
    competitionOrderFn.value(a.competition.code, b.competition.code)
  );
});

const possibleEntriesPrep = computed(() => {
  const possibleVals =
    initRegistrationSubstituteAthletesForm.value.formFields
      .find(x => x.name == "substituteEntryIds")
      ?.possibleValues?.map(x => x.value) ?? [];
  return entries.value.filter(e => possibleVals.includes(e.id));
});

const possibleEventProductsPrep = computed(() => {
  const possibleVals =
    initRegistrationSubstituteAthletesForm.value.formFields
      .find(x => x.name == "substituteRegistrationEventProductIds")
      ?.possibleValues?.map(x => x.value) ?? [];
  return sortBy(
    cloneDeep(
      registration.value?.registrationEventProducts?.filter(e =>
        possibleVals.includes(e.id)
      ) ?? []
    ),
    "eventProduct.name"
  );
});

const possibleChanges = computed(() => {
  const possibleEntries = possibleEntriesPrep.value.map(e => {
    e.athlete = registration.value.athletes[0];
    e.horse = registration.value.horses[0];
    if (form.value.substituteEntryIds.includes(e.id)) {
      e.athlete = form.value.substituteWithPerson;
    }
    return e;
  });

  const possibleProducts = possibleEventProductsPrep.value.map(e => {
    e.athlete = registration.value.athletes[0];
    e.horse = registration.value.horses[0];
    if (form.value.substituteRegistrationEventProductIds.includes(e.id)) {
      e.athlete = form.value.substituteWithPerson;
    }
    return e;
  });

  return { possibleEntries, possibleProducts };
});

const { onResult, loading: initFormLoading } = useQuery(
  gql`
    query initRegistrationSubstituteAthletesForm(
      $substituteRegistrationEventProductIds: [ID!]
      $substituteEntryIds: [ID!]
      $substituteWithPersonId: ID
      $substituteInRegistrationId: ID
    ) {
      initRegistrationSubstituteAthletesForm(
        substituteRegistrationEventProductIds: $substituteRegistrationEventProductIds
        substituteEntryIds: $substituteEntryIds
        substituteWithPersonId: $substituteWithPersonId
        substituteInRegistrationId: $substituteInRegistrationId
      ) {
        formFields {
          name
          possibleValues
        }
        competitionsFeedbackMessages {
          competitionId
          feedbackMessages {
            ${DisplayTextFragments.translation}
          }
        }
        submitNotAllowed
      }
    }
  `,
  () => ({
    substituteRegistrationEventProductIds:
      form.value.substituteRegistrationEventProductIds,
    substituteEntryIds: form.value.substituteEntryIds,
    substituteWithPersonId: form.value.substituteWithPersonId,
    substituteInRegistrationId: form.value.substituteInRegistrationId
  }),
  () => ({
    enabled: dialogValue.value === true,
    fetchPolicy: "network-only"
  })
);

onResult(({ loading, data }) => {
  if (loading) return;
  initRegistrationSubstituteAthletesForm.value =
    data.initRegistrationSubstituteAthletesForm;
});

async function openFeiActionForm({ entry: e }) {
  registrationLoading.value = true;

  form.value = {
    substituteRegistrationEventProductIds: [],
    substituteEntryIds: [],
    substituteWithPersonId: null,
    substituteWithPerson: null,
    substituteInRegistrationId: e.registrationId
  };

  dialogValue.value = true;

  try {
    const { data } = await apolloClient.query({
      query: gql`
        query AthleteSubstituteRegistration($id: ID) {
          registration(id: $id) {
            id
            eventId
            event {
              id
              ${AppShowEventDetails.fragments.event}
            }
            athletes {
              id
              ${AppEntryRegistrationOverview.fragments.personFields}
              ${AppShowPersonNameWithIdentifiers.fragments.person}
            }
            horses {
              id
              ${AppShowHorseName.fragments.horse}
              ${AppEntryRegistrationOverview.fragments.horseFields}
            }
            entries {
              id
              ${AppShowEntryClassChip.fragments.entryFields}
              competition {
                id
                code
                name
                difficultyLevelId
                difficultyLevel @client {
                  id
                  displayName
                }
                competitionRuleId
                competitionRule @client {
                  id
                  name
                }
              }
            }
            registrationEventProducts {
              id
              eventProduct {
                id
                name
              }
            }
          }
        }
      `,
      variables: {
        id: e.registrationId
      },
      fetchPolicy: "network-only"
    });
    registration.value = data.registration;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    registrationLoading.value = false;
  }
}

function showEntryCompetitionDetails(e) {
  return [
    e.code,
    e.name,
    e.difficultyLevel?.displayName,
    e.competitionRule?.name
  ]
    .filter(Boolean)
    .join(" | ");
}

async function submit() {
  formSubmitted.value = true;
  blockRegistrationQuery.value = true;
  try {
    const query = registrationQuery.query();
    const variables = registrationQuery.variables()?.value ?? {};

    const formVars = form.value;
    if (variables.includeUnregistered != undefined) {
      formVars.includeUnregistered = variables.includeUnregistered;
    }

    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation registrationSubstituteAthletes(
          ${
            ![null, undefined].includes(variables.includeUnregistered)
              ? "$includeUnregistered: Boolean"
              : ""
          }
          ${
            ![null, undefined].includes(variables.entryFilter)
              ? "$entryFilter: EntryFilterInput"
              : ""
          }
          ${
            ![null, undefined].includes(variables.paradressageGrades)
              ? "$paradressageGrades: [String]"
              : ""
          }
          ${
            ![null, undefined].includes(variables.registrationHorseFilter)
              ? "$registrationHorseFilter: RegistrationHorseFilterInput"
              : ""
          }
          ${
            ![null, undefined].includes(variables.registrationPersonFilter)
              ? "$registrationPersonFilter: RegistrationPersonFilterInput"
              : ""
          }
          ${
            ![null, undefined].includes(
              variables.registrationEventProductFilter
            )
              ? "$registrationEventProductFilter: RegistrationEventProductFilterInput"
              : ""
          }
          $substituteWithPersonId: ID
          $substituteRegistrationEventProductIds: [ID!]
          $substituteInRegistrationId: ID!
          $substituteEntryIds: [ID!]
        ) {
          registrationSubstituteAthletes(
            input: {
              substituteWithPersonId: $substituteWithPersonId
              substituteRegistrationEventProductIds: $substituteRegistrationEventProductIds
              substituteInRegistrationId: $substituteInRegistrationId
              substituteEntryIds: $substituteEntryIds
            }
          ) {
            registration {
              id
              ${
                fragments.registrationFragment
                  ? fragments.registrationFragment()
                  : ""
              }
            }
            initialRegistration {
              id
              ${
                fragments.registrationFragment
                  ? fragments.registrationFragment()
                  : ""
              }
            }
          }
        }
      `,
      variables: formVars,
      update(cache, { data }) {
        try {
          const newRegistration =
            data.registrationSubstituteAthletes.registration;
          const oldRegistration =
            data.registrationSubstituteAthletes.initialRegistration;

          cache.updateQuery(
            {
              query,
              variables
            },
            data => {
              const regs = clone(
                data.event?.registrations ?? data.registrations
              );
              const regIds = regs.map(x => x.id);
              if (!regIds.includes(newRegistration.id)) {
                const indexOfInitialReg = regIds.indexOf(oldRegistration.id);
                // if unregistered are included or old reg still not cancelled then update old reg and add new reg
                if (
                  variables.includeUnregistered ||
                  oldRegistration.status != "cancelled"
                ) {
                  regs[indexOfInitialReg] = oldRegistration; // force update old registration
                  regs.splice(indexOfInitialReg + 1, 0, newRegistration);
                } else {
                  // if cancelled, replace old reg to new reg
                  regs[indexOfInitialReg] = newRegistration;
                }

                if (data.event) {
                  return {
                    event: {
                      ...data.event,
                      registrations: regs
                    }
                  };
                }
                return {
                  registrations: regs
                };
              }
            }
          );
        } catch (error) {
          console.error(error);
        }
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
    blockRegistrationQuery.value = false;
  }
}

defineExpose({
  openFeiActionForm,
  submit // test purposes exposed
});
</script>
